import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { FontText, MarkdownText, PrimaryButton } from '../common';
import { useTranslation } from 'react-i18next';

interface ChapterContentProps {
  content: string | null;
  preview?: string;
  isLocked: boolean;
  isAuthenticated: boolean;
  showUnlockPrompt?: boolean;
  userCoins?: number;
  chapterCoins?: number;
  autoUnlockEnabled?: boolean;
  onUnlock?: () => void;
  onBuyCoins?: () => void;
  onAutoUnlockToggle?: () => void;
  fontSize: number;
  chapter?: {
    number: number;
    title: string;
  };
}

export const ChapterContent: React.FC<ChapterContentProps> = ({
  content,
  preview,
  isLocked,
  isAuthenticated,
  showUnlockPrompt = false,
  userCoins = 0,
  chapterCoins = 30,
  autoUnlockEnabled = false,
  onUnlock,
  onBuyCoins,
  onAutoUnlockToggle,
  fontSize,
  chapter,
}) => {
  const { theme } = useTheme();
  const { t } = useTranslation();

  const styles: Record<string, React.CSSProperties> = {
    container: {
      maxWidth: 800,
      margin: '0 auto',
      padding: '0 20px',
    },
    chapterTitle: {
      color: theme.colors.text,
      fontSize: 24,
      fontWeight: 600,
      marginBottom: 20,
      margin: '0 0 20px 0',
    },
    content: {
      marginBottom: '30px',
    },
    preview: {
      marginBottom: '20px',
    },
    lockedContainer: {
      backgroundColor: theme.colors.surface,
      padding: '30px 20px',
      borderRadius: '12px',
      textAlign: 'center',
      margin: '20px 0',
      marginBottom: '100px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    },
    lockedTitle: {
      color: theme.colors.text,
      marginBottom: '10px',
    },
    lockedDescription: {
      color: theme.colors.textSecondary,
      marginBottom: '20px',
    },
    autoUnlockContainer: {
      backgroundColor: theme.colors.overlay,
      padding: '10px',
      borderRadius: '8px',
      marginTop: '15px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    autoUnlockText: {
      color: theme.colors.textSecondary,
      marginLeft: '2px',
      marginBottom: '2px',
    },
    coinsInfo: {
      marginTop: '10px',
      color: theme.colors.text,
    },
  };

  if (!isAuthenticated && isLocked) {
    return (
      <div style={styles.container}>
        {chapter && (
          <FontText h2 style={styles.chapterTitle}>
            {t('chapter_label', { number: chapter.number })}: {chapter.title}
          </FontText>
        )}
        {preview && (
          <MarkdownText fontSize={fontSize} style={styles.preview}>
            {preview}
          </MarkdownText>
        )}
        <div style={styles.lockedContainer}>
          <FontText h3 style={styles.lockedTitle}>
            {t('continue_reading')}
          </FontText>
          <FontText normal style={styles.lockedDescription}>
            {t('continue_reading_description')}
          </FontText>
          <PrimaryButton onClick={onBuyCoins} size="large">
            {t('continue_reading')}
          </PrimaryButton>
        </div>
      </div>
    );
  }

  if (isLocked || showUnlockPrompt) {
    const canUnlock = userCoins >= chapterCoins;

    return (
      <div style={styles.container}>
        {chapter && (
          <FontText h2 style={styles.chapterTitle}>
            {t('chapter_label', { number: chapter.number })}: {chapter.title}
          </FontText>
        )}
        {preview && (
          <MarkdownText fontSize={fontSize} style={styles.preview}>
            {preview}
          </MarkdownText>
        )}
        <div style={styles.lockedContainer}>
          <FontText h3 style={styles.lockedTitle}>
            {t('chapter_locked')}
          </FontText>
          <FontText normal style={styles.lockedDescription}>
            {t('chapter_requires_coins', { coins: chapterCoins })}
          </FontText>
          <FontText small style={styles.coinsInfo}>
            {t('you_have_coins', { coins: userCoins })}
          </FontText>

          <div style={{ marginTop: '20px' }}>
            {canUnlock ? (
              <PrimaryButton onClick={onUnlock} style={{ marginRight: '10px' }}>
                {t('unlock_with_coins_count', { coins: chapterCoins })}
              </PrimaryButton>
            ) : (
              <PrimaryButton onClick={onBuyCoins}>
                {t('buy_coins')}
              </PrimaryButton>
            )}
          </div>

          {onAutoUnlockToggle && (
            <div style={styles.autoUnlockContainer}>
              <label>
                <input
                  type="checkbox"
                  checked={autoUnlockEnabled}
                  onChange={onAutoUnlockToggle}
                />
              </label>

              <FontText small style={styles.autoUnlockText}>
                {t('auto_unlock_chapters')}
              </FontText>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {chapter && (
        <FontText h2 style={styles.chapterTitle}>
          {t('chapter_label', { number: chapter.number })}: {chapter.title}
        </FontText>
      )}
      {content && (
        <MarkdownText fontSize={fontSize} style={styles.content}>
          {content}
        </MarkdownText>
      )}
    </div>
  );
};
